import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const annotationCreatedBySchema = z.object({
  email: z.string(),
  username: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  is_staff: z.boolean(),
});

const annotationUpdatedBySchema = z.object({
  email: z.string(),
  username: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  is_staff: z.boolean(),
});

const annotationAdvertisementSchema = z.object({
  id: z.number(),
  name: z.string(),
  category_id: z.number(),
});

const annotationProductSchema = z.object({
  id: z.number(),
  asin: z.string(),
  product_image_url: z.string().optional(),
  sku: z.string(),
  product_title: z.string(),
});

const annotationMetaItemSchema = z.object({
  change: z.union([z.number(), z.boolean()]),
  previous: z.union([z.number(), z.boolean()]),
  current: z.union([z.number(), z.boolean()]),
});

const annotationSchema = z.object({
  id: z.number(),
  created_by: annotationCreatedBySchema.nullable(),
  updated_by: annotationUpdatedBySchema.nullable(),
  advertisement: annotationAdvertisementSchema.nullable(),
  product: annotationProductSchema.nullable().optional(),
  category: z.unknown().nullable(),
  created_on: z.string(),
  updated_on: z.string(),
  deleted_on: z.string().nullable(),
  channel: z.string(),
  annotation_type: z.string(),
  marketplace: z.string(),
  ts: z.string(),
  description: z.string().nullable(),
  meta: z.record(annotationMetaItemSchema).optional(),
  stats: z.unknown(),
  key: z.string().nullable(),
  deleted_by: z.string().nullable(),
  content_template: z.unknown(),
  pricing_plan: z.unknown(),
  promotion_result: z.unknown(),
});

export type Annotation = z.infer<typeof annotationSchema>;

const annotationDateGroupSchema = z.object({
  date: z.string(),
  annotations: z.array(annotationSchema),
});

export const annotationsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getAnnotations",
    method: "get",
    path: "/annotations/",
    parameters: [
      {
        name: "groupby",
        type: "Query",
        schema: z.enum(["day", "week", "month"]).optional(),
      },
      {
        name: "ts_after",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "ts_before",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "category",
        type: "Query",
        schema: z.union([z.string(), z.number()]).nullable().optional(),
      },
      {
        name: "product",
        type: "Query",
        schema: z.union([z.string(), z.number()]).nullable().optional(),
      },
      {
        name: "advertisement",
        type: "Query",
        schema: z.union([z.string(), z.number()]).nullable().optional(),
      },
      {
        name: "force_start_dates",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    status: 200,
    response: z.array(annotationDateGroupSchema),
  })
    .addEndpoint({
      alias: "createAnnotation",
      method: "post",
      path: "/annotations/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            marketplace: z.string(),
            description: z.string(),
            ts: z.string(),
            category: z.union([z.string(), z.number()]).nullable().optional(),
            product: z.union([z.string(), z.number()]).nullable().optional(),
            advertisement: z.union([z.string(), z.number()]).nullable().optional(),
          }),
        },
      ],
      status: 201,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAnnotation",
      method: "put",
      path: "/annotations/:id/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
        {
          name: "body",
          type: "Body",
          schema: z.object({
            marketplace: z.string(),
            description: z.string().nullable(),
            ts: z.string(),
            category: z.union([z.string(), z.number()]).nullable().optional(),
          }),
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "deleteAnnotation",
      method: "delete",
      path: "/annotations/:id/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
      ],
      status: 204,
      response: z.void(),
    })
    .build(),
);
